import { getSsrRequestLocals } from "@/config/ssr-request-locals";
import { resolveSignUpGeoNewsletterSubscribeUrl } from "@/utils/sign-up-geo-restriction";

export type UiBrandingLogoVariant = {
  src?: string;
  altText?: string;
  href?: string;
};

export type UiBrandingLogoSet = {
  light?: UiBrandingLogoVariant;
  dark?: UiBrandingLogoVariant;
};

/** Browser tab / PWA icon URLs (resolved in `server/views/layout.pug`). */
export type UiBrandingIcons = {
  themeColor?: string;
  png96?: string;
  svg?: string;
  shortcutIcon?: string;
  appleTouchIcon?: string;
  maskIcon?: string;
  manifest?: string;
  msTileImage?: string;
};

export type UiBrandingFont = {
  /** CSS font family name (e.g. "Inter", "Source Sans 3"). Used for --font-sans. */
  family?: string;
  /**
   * Google Fonts spec for dynamic import (e.g. "Inter:ital,wght@0,400;0,700").
   * If a full URL (http/https), it is used as-is; otherwise built as fonts.googleapis.com/css2?family=...
   */
  google?: string;
};

/** Appearance modes users can pick in the UI. Loaded from theme JSON (`window.__UI_BRANDING__`). */
export type ThemeModeVariant = "light" | "dark" | "system";

const ALL_THEME_MODE_VARIANTS: ThemeModeVariant[] = ["light", "dark", "system"];

function isThemeModeVariant(value: unknown): value is ThemeModeVariant {
  return value === "light" || value === "dark" || value === "system";
}

/** User-facing instrument identifier nouns for console copy (ticker vs ISIN, etc.). */
export type UiBrandingSymbology = {
  /** Mid-sentence singular, e.g. "symbol" | "ISIN". */
  singular: string;
  /** Mid-sentence plural, e.g. "symbols" | "ISINs". */
  plural: string;
  /** Optional examples for add/filter placeholders. */
  examples?: string[];
};

export type UiBranding = {
  companyName: string;
  productName: string;
  /** 1200x630 PNG for link previews (og:image). Per theme; a brand without one gets a text-only card. */
  socialImage?: string;
  /**
   * Display nouns for the instrument identifier used in this brand's universe UI.
   * Defaults to symbol/symbols with ticker examples when omitted.
   */
  symbology?: UiBrandingSymbology;
  /**
   * A free-form identifier for the current brand theme.
   * This can be used as a semantic value for analytics or conditional UI.
   */
  theme?: string;
  /**
   * Which appearance modes (light / dark / system) the user may choose.
   * If exactly one entry is set, the theme toggle is hidden and that mode is always applied.
   */
  themeModeVariants?: ThemeModeVariant[];
  /** Optional font configuration; when set, applies to Tailwind font-sans and can trigger a Google Font link. */
  font?: UiBrandingFont;
  icons?: UiBrandingIcons;
  logo?: {
    horizontal?: UiBrandingLogoSet;
    mark?: UiBrandingLogoSet;
  };
  solid?: boolean;
  /** Optional "Powered by viaNexus" attribution (white-label themes). */
  poweredBy?: {
    enabled?: boolean;
    href?: string;
    /** Fallback text when logo is unavailable; also used as logo alt text. */
    label?: string;
    logo?: UiBrandingLogoSet;
  };
  catalog?: {
    footerCTA?: {
      slogan?: string;
      subtitle?: string;
      ctaLabel?: string;
    };
    slogan?: string;
    sloganSubtitle?: string;
    /**
     * Optional banner on the public catalog home, shown above pricing / platform products.
     * Copy is trusted (theme JSON); use `descriptionHtml` only with controlled config.
     */
    promotion?: {
      /** When true, the promotion section is rendered. */
      enabled?: boolean;
      /** Small uppercase-style label above the title (e.g. "Launch promotion"). */
      eyebrow?: string;
      title?: string;
      /** Trusted HTML for the body (e.g. <strong>2 months</strong>). */
      descriptionHtml?: string;
      ctaLabel?: string;
    };
    /**
     * Optional marketing defaults for the enterprise tier card on tiered marketplace products
     * (when `MarketplaceTieredProductCard` receives `showEnterpriseDealCardIfAvailable` and the product has `enterpriseDealAvailable`).
     */
    enterpriseDealCard?: {
      /** When `true`, the enterprise card may show even if `features.payByInvoice` is false. */
      enabled?: boolean;
      eyebrow?: string;
      title?: string;
      description?: string;
      ctaLabel?: string;
      /** Bullet entitlements (same loose shape as `TierOrDealEntitlement`). */
      entitlements?: Array<{
        key?: string;
        name: string;
        displayValue?: string | null;
        booleanValue?: boolean | null;
        numericValue?: number | null;
        stringValue?: string | null;
        unit?: string | null;
        jsonValue?: unknown | null;
      }>;
    };
    /** Catalog platform product list filters (category / provider pills). Omitted flags default to enabled. */
    platformProductFilters?: {
      category?: { enabled?: boolean };
      provider?: { enabled?: boolean };
    };
    /** Shown on marketplace product cards when `features.disableCheckout` is enabled. */
    checkoutDisabled?: {
      message?: string;
    };
  };
  social?: {
    linkedin?: string;
    x?: string;
    facebook?: string;
    instagram?: string;
    tiktok?: string;
    reddit?: string;
    github?: string;
  };
  links?: {
    blog?: string;
    terms?: string;
    privacy?: string;
    cookie?: string;
    status?: string;
    home?: string;
    catalog?: string;
    docs?: string;
    console?: string;
    login?: string;
    register?: string;
    support?: string;
  };
  /** Optional footer newsletter subscribe link (catalog footer). */
  newsletter?: {
    enabled?: boolean;
    /** Relative path or http(s) URL opened when the user clicks subscribe. */
    href?: string;
    label?: string;
    /** Optional short line shown above the subscribe button. */
    message?: string;
  };
  components?: {
    badge?: {
      solid?: boolean;
    };
  };
  support?: {
    /** Show the "Email Support" card on the support page. Defaults to true. */
    emailCard?: boolean;
  };
  email?: {
    branding?: {
      supportEmail?: string;
      /** Sales/enterprise enquiries. Falls back to `supportEmail` when unset. */
      salesEmail?: string;
    };
  };
};

/** Whether badges use solid fills: `components.badge.solid` if set, otherwise root `solid`. */
export function getIsBadgeSolid(branding: UiBranding): boolean {
  return Boolean(branding.components?.badge?.solid || branding.solid);
}

declare global {
  interface Window {
    __UI_BRANDING__?: UiBranding;
  }
}

export const DEFAULT_SYMBOLOGY: Required<UiBrandingSymbology> = {
  singular: "symbol",
  plural: "symbols",
  examples: ["AAPL", "MSFT"],
};

export const DEFAULT_UI_BRANDING: UiBranding = {
  theme: "vianexus",
  companyName: "Blue-Sky Data Platform",
  productName: "viaNexus",
  symbology: { ...DEFAULT_SYMBOLOGY },
  logo: {
    horizontal: {
      light: { src: "/images/logos/ViaNexus-Horizontal-Logo.svg", altText: "viaNexus logo" },
      dark: { src: "/images/logos/ViaNexus-Horizontal-Logo-dark.svg", altText: "viaNexus logo" },
    },
    mark: {
      light: { src: "/images/console/logos/Logo-viaNexus.svg", altText: "viaNexus logo" },
      dark: { src: "/images/console/logos/Logo-viaNexus.svg", altText: "viaNexus logo" },
    },
  },
  catalog: {
    slogan: `Premium data for the <br /><span class="text-primary">next generation</span> of FinTech.`,
    sloganSubtitle:
      "Access institution-grade financial datasets and real-time market signals through a single, unified API. Built for developers and AI, trusted by funds.",
    footerCTA: {
      slogan: "Unlock institutional-grade data. <br /><span class='text-primary'>Start building in minutes.</span>",
      subtitle:
        "Join developers and funds who ship faster with unified market data and real-time signals—all through one API.",
      ctaLabel: "Create your free account",
    },
    platformProductFilters: {
      category: { enabled: true },
      provider: { enabled: true },
    },
  },
  components: {
    badge: {
      solid: false,
    },
  },
  links: {
    home: "https://vianexus.com/",
    catalog: "/catalog",
    docs: "/docs",
    console: "/console",
    login: "/auth",
    register: "/auth/register",
    support: "/catalog/support",
  },
  email: {
    branding: {
      supportEmail: "support@vianexus.com",
    },
  },
};

const mergeLogoVariant = (
  fallback?: UiBrandingLogoVariant,
  override?: UiBrandingLogoVariant
): UiBrandingLogoVariant => {
  return {
    src: override?.src ?? fallback?.src,
    altText: override?.altText ?? fallback?.altText,
    href: override?.href ?? fallback?.href,
  };
};

const mergeLogoSet = (fallback?: UiBrandingLogoSet, override?: UiBrandingLogoSet): UiBrandingLogoSet => {
  return {
    light: mergeLogoVariant(fallback?.light, override?.light),
    dark: mergeLogoVariant(fallback?.dark, override?.dark),
  };
};

const mergeUiBranding = (fallback: UiBranding, override?: UiBranding): UiBranding => {
  return {
    ...override,
    theme: override?.theme ?? fallback.theme,
    themeModeVariants: override?.themeModeVariants ?? fallback.themeModeVariants,
    font: override?.font ?? fallback.font,
    catalog: {
      slogan: override?.catalog?.slogan ?? fallback.catalog?.slogan,
      sloganSubtitle: override?.catalog?.sloganSubtitle ?? fallback.catalog?.sloganSubtitle,
      footerCTA: override?.catalog?.footerCTA ?? fallback.catalog?.footerCTA,
      promotion:
        fallback.catalog?.promotion || override?.catalog?.promotion
          ? {
              ...fallback.catalog?.promotion,
              ...override?.catalog?.promotion,
            }
          : undefined,
      enterpriseDealCard:
        fallback.catalog?.enterpriseDealCard || override?.catalog?.enterpriseDealCard
          ? {
              ...fallback.catalog?.enterpriseDealCard,
              ...override?.catalog?.enterpriseDealCard,
            }
          : undefined,
      platformProductFilters:
        fallback.catalog?.platformProductFilters || override?.catalog?.platformProductFilters
          ? {
              category: {
                enabled:
                  override?.catalog?.platformProductFilters?.category?.enabled ??
                  fallback.catalog?.platformProductFilters?.category?.enabled,
              },
              provider: {
                enabled:
                  override?.catalog?.platformProductFilters?.provider?.enabled ??
                  fallback.catalog?.platformProductFilters?.provider?.enabled,
              },
            }
          : undefined,
    },
    poweredBy:
      fallback.poweredBy || override?.poweredBy
        ? {
            ...fallback.poweredBy,
            ...override?.poweredBy,
            logo: mergeLogoSet(fallback.poweredBy?.logo, override?.poweredBy?.logo),
          }
        : undefined,
    logo: {
      horizontal: mergeLogoSet(fallback.logo?.horizontal, override?.logo?.horizontal),
      mark: mergeLogoSet(fallback.logo?.mark, override?.logo?.mark),
    },
    links: {
      ...fallback.links,
      ...override?.links,
    },
    newsletter:
      fallback.newsletter || override?.newsletter
        ? {
            ...fallback.newsletter,
            ...override?.newsletter,
          }
        : undefined,
    symbology: {
      singular: override?.symbology?.singular ?? fallback.symbology?.singular ?? DEFAULT_SYMBOLOGY.singular,
      plural: override?.symbology?.plural ?? fallback.symbology?.plural ?? DEFAULT_SYMBOLOGY.plural,
      examples: override?.symbology?.examples ?? fallback.symbology?.examples ?? DEFAULT_SYMBOLOGY.examples,
    },
    components: {
      badge: {
        solid: override?.components?.badge?.solid ?? fallback.components?.badge?.solid,
      },
    },
    support: {
      emailCard: override?.support?.emailCard ?? fallback.support?.emailCard,
    },
    email: {
      branding: {
        supportEmail: override?.email?.branding?.supportEmail ?? fallback.email?.branding?.supportEmail,
        salesEmail: override?.email?.branding?.salesEmail ?? fallback.email?.branding?.salesEmail,
      },
    },
  };
};

export const getUiBranding = (): UiBranding => {
  if (typeof window === "undefined") {
    return mergeUiBranding(DEFAULT_UI_BRANDING, getSsrRequestLocals()?.uiBranding);
  }

  return mergeUiBranding(DEFAULT_UI_BRANDING, window.__UI_BRANDING__);
};

export type FooterNewsletterLink = {
  href: string;
  label: string;
  message?: string;
  external: boolean;
};

/** Resolved footer newsletter link when `newsletter.enabled` and a valid `href` are set. */
export function getFooterNewsletterLink(): FooterNewsletterLink | null {
  const config = getUiBranding().newsletter;
  if (!config?.enabled) {
    return null;
  }
  const href = resolveSignUpGeoNewsletterSubscribeUrl(config.href);
  if (!href) {
    return null;
  }
  const label = config.label?.trim() || "Subscribe to newsletter";
  const message = config.message?.trim() || undefined;
  return {
    href,
    label,
    message,
    external: /^https?:\/\//i.test(href),
  };
}

/** Support contact address from theme branding (`email.branding.supportEmail`). */
export function getSupportEmail(): string {
  const email = getUiBranding().email?.branding?.supportEmail?.trim();
  return email || "support@vianexus.com";
}

/** Whether the support page shows the "Email Support" card (`support.emailCard`, default true). */
export function isEmailSupportCardEnabled(): boolean {
  return getUiBranding().support?.emailCard !== false;
}

/**
 * Sales/enterprise enquiry address (`email.branding.salesEmail`), falling back to the support
 * address for brands that run a single mailbox.
 */
export function getSalesEmail(): string {
  return getUiBranding().email?.branding?.salesEmail?.trim() || getSupportEmail();
}

/** External URL for brand logo clicks (`links.home`). */
export function getBrandHomeUrl(): string {
  return getUiBranding().links?.catalog?.trim() || "/catalog";
}

/** Sign-up URL from theme branding (`links.register`), or the console's own register page. */
export function getRegisterUrl(): string {
  return getUiBranding().links?.register?.trim() || "/auth/register";
}

/** Privacy-policy URL from theme branding (`links.privacy`), or "" when unset. */
export function getPrivacyUrl(): string {
  return getUiBranding().links?.privacy?.trim() || "";
}

/** Cookie-policy URL from theme branding (`links.cookie`), or "" when unset. */
export function getCookieUrl(): string {
  return getUiBranding().links?.cookie?.trim() || "";
}

/** Copyright line for footers and sidebars (e.g. "© 2026 SIX. All rights reserved."). */
export function getCopyrightNotice(year = new Date().getFullYear()): string {
  return `© ${year} ${getUiBranding().companyName}. All rights reserved.`;
}

export const DEFAULT_POWERED_BY_HREF = "https://vianexus.com";
export const DEFAULT_POWERED_BY_LABEL = "viaNexus";
export const DEFAULT_POWERED_BY_LOGO: UiBrandingLogoSet = {
  light: { src: "/images/logos/ViaNexus-Horizontal-Logo.png", altText: "viaNexus" },
  dark: { src: "/images/logos/ViaNexus-Horizontal-Logo-dark.png", altText: "viaNexus" },
};

export type PoweredByBranding = {
  href: string;
  label: string;
  logo: { src: string; altText: string };
  enabled: boolean;
};

/** Resolved powered-by link when `poweredBy.enabled` is set in branding; otherwise `null`. */
export function getPoweredBy(isDarkMode = false): PoweredByBranding | null {
  const config = getUiBranding().poweredBy;
  if (!config?.enabled) {
    return null;
  }
  const label = config.label?.trim() || DEFAULT_POWERED_BY_LABEL;
  const logoSet = config.logo ?? DEFAULT_POWERED_BY_LOGO;
  const logoVariant = isDarkMode ? logoSet.dark : logoSet.light;
  const fallbackVariant = isDarkMode ? DEFAULT_POWERED_BY_LOGO.dark : DEFAULT_POWERED_BY_LOGO.light;

  return {
    enabled: config.enabled,
    href: config.href?.trim() || DEFAULT_POWERED_BY_HREF,
    label,
    logo: {
      src: logoVariant?.src?.trim() || fallbackVariant?.src || "",
      altText: logoVariant?.altText?.trim() || label,
    },
  };
}

/** @deprecated Use {@link getPoweredBy} */
export const getCatalogPoweredBy = getPoweredBy;

/**
 * Resolved list of allowed appearance modes from branding config.
 * Invalid entries are dropped; empty or missing config defaults to light, dark, and system.
 */
export function getThemeModeVariants(): ThemeModeVariant[] {
  const raw = getUiBranding().themeModeVariants;
  if (!raw || !Array.isArray(raw)) {
    return [...ALL_THEME_MODE_VARIANTS];
  }
  const ordered: ThemeModeVariant[] = [];
  for (const item of raw) {
    if (isThemeModeVariant(item) && !ordered.includes(item)) {
      ordered.push(item);
    }
  }
  return ordered.length > 0 ? ordered : [...ALL_THEME_MODE_VARIANTS];
}

/** Resolved catalog platform product filter visibility from branding (defaults: both enabled). */
export function getCatalogPlatformProductFilters(): { category: boolean; provider: boolean } {
  const filters = getUiBranding().catalog?.platformProductFilters;
  return {
    category: filters?.category?.enabled !== false,
    provider: filters?.provider?.enabled !== false,
  };
}

/** Title form for a symbology noun: "symbol" → "Symbol"; acronyms like "ISIN" stay unchanged. */
export function titleSymbologyNoun(noun: string): string {
  if (!noun) return noun;
  if (noun === noun.toUpperCase()) return noun;
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

/** Indefinite article for a symbology noun, chosen by initial sound ("a symbol" / "an ISIN"). */
export function symbologyArticle(noun: string): string {
  return /^[aeiou]/i.test(noun) ? "an" : "a";
}

export type SymbologyLabels = {
  singular: string;
  plural: string;
  examples: string[];
  /** Title-cased singular for headings ("Symbol" / "ISIN"). */
  singularTitle: string;
  /** Title-cased plural for labels ("Symbols" / "ISINs"). */
  pluralTitle: string;
  /** Singular prefixed with its indefinite article ("a symbol" / "an ISIN"). */
  singularWithArticle: string;
};

/** Resolved instrument-identifier nouns for universe UI copy. */
export function getSymbologyLabels(): SymbologyLabels {
  const config = getUiBranding().symbology;
  const singular = config?.singular?.trim() || DEFAULT_SYMBOLOGY.singular;
  const plural = config?.plural?.trim() || DEFAULT_SYMBOLOGY.plural;
  const examples = config?.examples && config.examples.length > 0 ? config.examples : DEFAULT_SYMBOLOGY.examples;
  return {
    singular,
    plural,
    examples,
    singularTitle: titleSymbologyNoun(singular),
    pluralTitle: titleSymbologyNoun(plural),
    singularWithArticle: `${symbologyArticle(singular)} ${singular}`,
  };
}
